import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  Zap, ChevronRight, Download, Car, Cpu, FileText, 
  Calendar, Tag, ShoppingCart, Check, X, Loader2,
  CreditCard, Mail, ChevronLeft
} from 'lucide-react';
import Footer from '../components/Footer';
import { useCanonical } from '../hooks/useCanonical';
import { BACKEND_URL } from '../config/api';

const API_URL = BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const LatestFilesPage = () => {
  const { fileId } = useParams();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Purchase state
  const [purchaseType, setPurchaseType] = useState(null); // 'original' or 'modified'
  const [showPayment, setShowPayment] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState([]);

  // Canonical URL
  useCanonical(fileId 
    ? `https://ecuflashservice.com/latest-files/${fileId}`
    : 'https://ecuflashservice.com/latest-files'
  );

  useEffect(() => {
    if (fileId) {
      fetchSingleFile(fileId);
    } else {
      fetchFiles();
    }
  }, [fileId, page]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/latest-files?page=${page}&page_size=12`);
      const data = await response.json();
      setFiles(data.files || []);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleFile = async (id) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/latest-files/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedFile(data);
      }
    } catch (error) {
      console.error('Error fetching file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = (type) => {
    setPurchaseType(type);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/latest-files/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file_id: selectedFile.id,
          file_type: purchaseType,
          email: customerEmail,
          paypal_order_id: orderId
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setDownloadUrls(data.download_urls);
        setPurchaseComplete(true);
        setShowPayment(false);
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Purchase failed. Please contact support.');
    }
  };

  const getPrice = () => {
    if (!selectedFile) return 0;
    return purchaseType === 'original' 
      ? selectedFile.original_price 
      : selectedFile.modified_price;
  };

  // Single file view
  if (fileId && selectedFile) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Helmet>
          <title>{selectedFile.calibration_no} - {selectedFile.vehicle_details} | ECU Flash Service</title>
          <meta name="description" content={`Download ${selectedFile.modification_type} ECU file for ${selectedFile.vehicle_details}. ${selectedFile.ecu_type} calibration available.`} />
          
          {/* Product Schema */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": `${selectedFile.calibration_no} - ${selectedFile.vehicle_details}`,
              "description": selectedFile.description || `ECU tuning file for ${selectedFile.vehicle_details}`,
              "image": selectedFile.thumbnail_url || "https://ecuflashservice.com/favicon-512.png",
              "brand": { "@type": "Brand", "name": selectedFile.brand || "ECU Flash Service" },
              "sku": selectedFile.id,
              "offers": [{
                "@type": "Offer",
                "name": "Original File",
                "price": selectedFile.original_price,
                "priceCurrency": "USD",
                "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                "availability": "https://schema.org/InStock",
                "url": `https://ecuflashservice.com/latest-files/${selectedFile.id}`
              }, {
                "@type": "Offer",
                "name": `Modified File (${selectedFile.modification_type})`,
                "price": selectedFile.modified_price,
                "priceCurrency": "USD",
                "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                "availability": "https://schema.org/InStock",
                "url": `https://ecuflashservice.com/latest-files/${selectedFile.id}`
              }]
            })}
          </script>
        </Helmet>

        {/* Header */}
        <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3 group">
                <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
                </div>
              </Link>
              <Link 
                to="/latest-files" 
                className="flex items-center gap-2 text-[#00F0FF] hover:text-white transition"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Latest Files
              </Link>
            </div>
          </div>
        </header>

        {/* File Details */}
        <main className="container mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - File Info */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
                <FileText className="w-4 h-4 text-[#00F0FF]" />
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Latest ECU File</span>
              </div>

              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                {selectedFile.calibration_no}
              </h1>

              <div className="text-xl text-[#888] mb-6">
                {selectedFile.vehicle_details}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-[#0A0A0A] border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-[#888] text-sm mb-1">
                    <Cpu className="w-4 h-4" />
                    ECU Type
                  </div>
                  <div className="text-white font-semibold">{selectedFile.ecu_type}</div>
                </div>
                <div className="bg-[#0A0A0A] border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-[#888] text-sm mb-1">
                    <Tag className="w-4 h-4" />
                    Modification
                  </div>
                  <div className="text-[#00F0FF] font-semibold">{selectedFile.modification_type}</div>
                </div>
                {selectedFile.brand && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-4">
                    <div className="flex items-center gap-2 text-[#888] text-sm mb-1">
                      <Car className="w-4 h-4" />
                      Brand
                    </div>
                    <div className="text-white font-semibold">{selectedFile.brand}</div>
                  </div>
                )}
                {selectedFile.year_range && (
                  <div className="bg-[#0A0A0A] border border-white/10 p-4">
                    <div className="flex items-center gap-2 text-[#888] text-sm mb-1">
                      <Calendar className="w-4 h-4" />
                      Year
                    </div>
                    <div className="text-white font-semibold">{selectedFile.year_range}</div>
                  </div>
                )}
              </div>

              {selectedFile.description && (
                <div className="text-[#888] mb-8">
                  {selectedFile.description}
                </div>
              )}

              {selectedFile.has_dcu && (
                <div className="bg-purple-500/10 border border-purple-500/30 px-4 py-3 rounded-lg mb-8">
                  <span className="text-purple-400 font-semibold">🔗 Includes DCU/SCR Files</span>
                  <p className="text-purple-300 text-sm mt-1">
                    This file comes bundled with Dosing Control Unit files
                  </p>
                  {selectedFile.dcu_calibration_no && (
                    <div className="mt-3 pt-3 border-t border-purple-500/20">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-purple-400/70 text-xs mb-1">DCU Calibration No</div>
                          <div className="text-purple-300 font-mono">{selectedFile.dcu_calibration_no}</div>
                        </div>
                        {selectedFile.dcu_ecu_type && (
                          <div>
                            <div className="text-purple-400/70 text-xs mb-1">DCU Type</div>
                            <div className="text-purple-300">{selectedFile.dcu_ecu_type}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right - Purchase Options */}
            <div>
              {purchaseComplete ? (
                <div className="bg-[#0A0A0A] border border-green-500/30 p-8 text-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Purchase Complete!</h3>
                  <p className="text-[#888] mb-6">Your download links are ready:</p>
                  <div className="space-y-3">
                    {downloadUrls.map((file, idx) => (
                      <a
                        key={idx}
                        href={file.url}
                        download={file.name}
                        className="flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-bold py-3 px-6 hover:bg-white transition"
                      >
                        <Download className="w-5 h-5" />
                        Download {file.name}
                      </a>
                    ))}
                  </div>
                </div>
              ) : showPayment ? (
                <div className="bg-[#0A0A0A] border border-white/10 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white">Complete Purchase</h3>
                    <button onClick={() => setShowPayment(false)} className="text-[#888] hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="bg-[#1A1A1A] p-4 mb-6">
                    <div className="text-[#888] text-sm mb-1">
                      {purchaseType === 'original' ? 'Original' : 'Modified'} File
                      {selectedFile.has_dcu && ' + DCU Bundle'}
                    </div>
                    <div className="text-2xl font-bold text-[#00F0FF]">${getPrice().toFixed(2)}</div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm text-[#888] mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email for download link
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 text-white placeholder-[#666] focus:border-[#00F0FF] focus:outline-none"
                      required
                    />
                  </div>

                  {customerEmail && (
                    <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", color: "blue", shape: "rect" }}
                        createOrder={(data, actions) => {
                          return actions.order.create({
                            purchase_units: [{
                              amount: { value: getPrice().toFixed(2) },
                              description: `${selectedFile.calibration_no} - ${purchaseType} file`
                            }]
                          });
                        }}
                        onApprove={(data, actions) => {
                          return actions.order.capture().then(() => {
                            handlePaymentSuccess(data.orderID);
                          });
                        }}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Original File Option */}
                  <div className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-green-500/50 transition">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[#888] text-sm">Original File</div>
                        <div className="text-2xl font-bold text-green-400">${selectedFile.original_price}</div>
                      </div>
                      <div className="bg-green-500/20 px-3 py-1 text-green-400 text-sm font-semibold">
                        STOCK
                      </div>
                    </div>
                    <p className="text-[#666] text-sm mb-4">
                      Unmodified original ECU calibration file
                      {selectedFile.has_dcu && ' + Original DCU file'}
                    </p>
                    <button
                      onClick={() => handlePurchase('original')}
                      className="w-full bg-green-500 text-white font-bold py-3 hover:bg-green-400 transition flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Buy Original
                    </button>
                  </div>

                  {/* Modified File Option */}
                  <div className="bg-[#0A0A0A] border border-[#00F0FF]/30 p-6 hover:border-[#00F0FF] transition">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-[#888] text-sm">Modified File ({selectedFile.modification_type})</div>
                        <div className="text-2xl font-bold text-[#00F0FF]">${selectedFile.modified_price}</div>
                      </div>
                      <div className="bg-[#00F0FF]/20 px-3 py-1 text-[#00F0FF] text-sm font-semibold">
                        TUNED
                      </div>
                    </div>
                    <p className="text-[#666] text-sm mb-4">
                      Professional {selectedFile.modification_type} calibration
                      {selectedFile.has_dcu && ' + Modified DCU file'}
                    </p>
                    <button
                      onClick={() => handlePurchase('modified')}
                      className="w-full bg-[#00F0FF] text-black font-bold py-3 hover:bg-white transition flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Buy Modified
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Files list view
  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Latest ECU Files | Professional Tuning Files | ECU Flash Service</title>
        <meta name="description" content="Browse our latest ECU tuning files. Professional calibrations for all major brands. Original and modified files available for instant download." />
      </Helmet>

      {/* Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h1 className="font-heading text-lg font-bold uppercase tracking-wide text-white">ECU Flash Service</h1>
              </div>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/file-store" className="text-[#888] hover:text-[#00F0FF] text-sm uppercase tracking-wider">
                File Store
              </Link>
              <Link to="/" className="bg-[#00F0FF] text-black px-6 py-2 font-bold text-sm uppercase tracking-wider hover:bg-white transition">
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] border border-[#00F0FF]/30 mb-6">
            <FileText className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00F0FF]">Fresh Calibrations</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">Latest</span>{' '}
            <span className="text-[#00F0FF]">ECU Files</span>
          </h1>
          <p className="text-[#888] text-lg max-w-2xl mx-auto">
            Browse our newest ECU calibration files. Professional tuning solutions available for instant download.
          </p>
        </div>
      </section>

      {/* Files Grid */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">📁</div>
              <div className="text-[#888]">No files available yet. Check back soon!</div>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {files.map((file) => (
                  <Link
                    key={file.id}
                    to={`/latest-files/${file.id}`}
                    className={`bg-[#0A0A0A] border p-6 group hover:border-[#00F0FF]/50 transition relative ${
                      file.is_featured ? 'border-[#FFD700]/50' : 'border-white/10'
                    }`}
                  >
                    {/* Featured Badge */}
                    {file.is_featured && (
                      <div className="absolute -top-3 left-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black text-xs font-bold px-3 py-1 uppercase tracking-wider">
                        ⭐ Featured
                      </div>
                    )}
                    
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="text-[#00F0FF] font-mono text-sm mb-1">{file.calibration_no}</div>
                        <div className="text-white font-semibold group-hover:text-[#00F0FF] transition">
                          {file.vehicle_details}
                        </div>
                      </div>
                      {file.has_dcu && (
                        <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-1">+DCU</span>
                      )}
                    </div>

                    {/* Details */}
                    <div className="text-[#666] text-sm mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Cpu className="w-4 h-4" />
                        {file.ecu_type}
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        {file.modification_type}
                      </div>
                      {/* DCU Details */}
                      {file.has_dcu && file.dcu_calibration_no && (
                        <div className="mt-2 pt-2 border-t border-white/5">
                          <div className="text-purple-400 text-xs font-semibold mb-1">DCU Bundle:</div>
                          <div className="flex items-center gap-2 text-purple-300/70">
                            <span className="font-mono text-xs">{file.dcu_calibration_no}</span>
                            {file.dcu_ecu_type && <span className="text-xs">• {file.dcu_ecu_type}</span>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Prices */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div>
                        <div className="text-xs text-[#888]">Original</div>
                        <div className="text-green-400 font-bold">${file.original_price}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[#888]">Modified</div>
                        <div className="text-[#00F0FF] font-bold">${file.modified_price}</div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#888] group-hover:text-[#00F0FF] transition" />
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-12">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-[#1A1A1A] text-white disabled:opacity-50 hover:bg-[#2A2A2A] transition"
                  >
                    Previous
                  </button>
                  <span className="text-[#888]">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-[#1A1A1A] text-white disabled:opacity-50 hover:bg-[#2A2A2A] transition"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default LatestFilesPage;
